<template>
  <label :class="['ms-checkbox', { 'event-none': readOnly }]">
    <input
      ref="checkboxInput"
      type="checkbox"
      class="ms-checkbox-control"
      :readOnly="readOnly"
      :checked="isChecked"
      :value="isChecked"
      :disabled="disabled"
      v-on="listeners"
    />
    <span v-if="label && isShowLabel" class="ms-checkbox--text">{{ label }}</span>
  </label>
</template>
<script>
import { defineComponent, computed, watch, ref, onMounted, nextTick } from 'vue';

export default defineComponent({
  name: 'msCheckbox',
  props: {
    modelValue: {
      default: false,
      type: [Boolean, Number],
    },
    label: {
      default: null,
      type: String,
    },
    disabled: {
      default: false,
      type: [Boolean, Function],
    },
    readOnly: {
      default: false,
      type: Boolean,
    },
    checked: {
      type: [Boolean, Number],
      default: false,
    },
    isShowLabel: {
      type: Boolean,
      default: true,
    },
    // Trạng thái nửa chọn (indeterminate)
    indeterminate: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    // Refs
    const checkboxInput = ref(null);
    const focused = ref(false);
    
    // Methods
    /**
     * Cập nhật trạng thái indeterminate cho checkbox
     */
    const updateIndeterminateState = () => {
      if (checkboxInput.value) {
        checkboxInput.value.indeterminate = props.indeterminate;
      }
    };
    
    /**
     * Xử lý khi focus vào checkbox
     */
    const onFocus = () => {
      focused.value = true;
    };
    
    /**
     * Xử lý khi blur khỏi checkbox
     */
    const onBlur = () => {
      focused.value = false;
    };

    /**
     * Hủy lan rộng event
     */
    const cancelEvent = (e) => {
      if (e) {
        if (typeof e.preventDefault === 'function') {
          e.preventDefault();
        }

        if (typeof e.stopPropagation === 'function') {
          e.stopPropagation();
        }

        if (typeof e.stopImmediatePropagation === 'function') {
          e.stopImmediatePropagation();
        }
      }
    };
    
    /**
     * Click vào checkbox
     */
    const onClick = (event) => {
      if (props.readOnly) {
        cancelEvent(event);
        return;
      }
      
      emit('click', event);
    };
    
    /**
     * Cập nhật giá trị khi input thay đổi
     */
    const onInput = () => {
      if (props.indeterminate) {
        // Khi đang ở trạng thái indeterminate, click đầu tiên sẽ chuyển thành checked
        emit('update:indeterminate', false);
        emit('update:modelValue', true);
      } else {
        emit('update:modelValue', !props.modelValue);
      }
    };
    
    /**
     * Xử lý sự kiện change
     */
    const onChange = (event) => {
      cancelEvent(event);
      emit('change', props.modelValue, props.label);
    };

    // Computed
    const isChecked = computed(() => props.modelValue || props.checked);
    
    const listeners = computed(() => {
      return {
        click: onClick,
        input: onInput,
        change: onChange,
        focus: onFocus,
        blur: onBlur,
      };
    });

    // Lifecycle hooks
    onMounted(() => {
      updateIndeterminateState();
    });
    
    // Watchers
    watch(() => props.indeterminate, () => {
      nextTick(() => {
        updateIndeterminateState();
      });
    });

    return {
      checkboxInput,
      focused,
      onFocus,
      onBlur,
      listeners,
      isChecked,
      updateIndeterminateState
    };
  }
});
</script>
<style lang="scss" scoped>
@import '@/assets/scss/components/MsCheckbox.scss';
</style>
